import {Injectable} from '@angular/core';
import {BaseService} from '../../base-service';
import {ApiConfiguration} from '../../api-configuration';
import {HttpClient, HttpHeaders, HttpRequest, HttpResponse} from '@angular/common/http';
import {Observable} from 'rxjs';
import {filter, map} from 'rxjs/operators';
import {StrictHttpResponse} from '../../strict-http-response';
import {GapQuoteDetailsResponse} from '../../models/gap-quote-details-response';
import {ResendRequest} from '../../models/resend-request';
import {ResendResponse} from '../../models/resend-response';
import {HealthPolicyMembers} from '../../models/health-policy-members';
import {GapPremiumsResponse} from '../../models/gap-premiums-response';
import {GapPremiumsRequest} from '../../models/gap-premiums-request';
import {GapSendQuoteRequest} from '../../models/gap-send-quote-request';
import {UpdateTcsRequest} from '../../models/update-tcs-request';

@Injectable({
  providedIn: 'root',
})
export class GapService extends BaseService {

  constructor(
    config: ApiConfiguration,
    http: HttpClient
  ) {
    super(config, http);
  }

  getGapQuotesDetails(searchTerm: string): Observable<GapQuoteDetailsResponse> {
    return this._getGapQuotesDetailsResponse(searchTerm).pipe(
      map(response => response.body as GapQuoteDetailsResponse)
    );
  }

  private _getGapQuotesDetailsResponse(searchTerm: string): Observable<StrictHttpResponse<GapQuoteDetailsResponse>> {
    const url = `${this.rootUrl}/gap/getGapQuotesDetails/${encodeURIComponent(searchTerm)}`;
    return this.http.get<GapQuoteDetailsResponse>(url, {
      observe: 'response'
    });
  }

  getHealthPolicyMembers(): Observable<HealthPolicyMembers> {
    return this._getHealthPolicyMembers().pipe(
      map(response => response.body as HealthPolicyMembers)
    );
  }

  private _getHealthPolicyMembers(): Observable<StrictHttpResponse<HealthPolicyMembers>> {
    const url = `${this.rootUrl}/gap/getHealthPolicyMembers`;
    return this.http.get<HealthPolicyMembers>(url, {
      observe: 'response'
    });
  }

  resendEmail(resendRequest: ResendRequest): Observable<ResendResponse> {
    return this._resendEmailPostResponse(resendRequest).pipe(
      map(response => response.body as ResendResponse)
    );
  }

  private _resendEmailPostResponse(resendRequest: ResendRequest): Observable<HttpResponse<ResendResponse>> {
    const req = new HttpRequest<ResendRequest>(
      'POST',
      `${this.rootUrl}/gap/resendGapEmail`,
      resendRequest,
      {
        headers: new HttpHeaders(),
        params: this.newParams(),
        responseType: 'json'
      }
    );

    return this.http.request<ResendResponse>(req).pipe(
      filter(event => event instanceof HttpResponse),
      map(response => response as HttpResponse<ResendResponse>)
    );
  }

  retrievePdfQuote(quoteId: string): any {
    const uri: string = this.rootUrl + `/gap/retrievePdf/${encodeURIComponent(quoteId)}`;

    const headers: HttpHeaders = new HttpHeaders({
      'Content-Type': 'application/json',
      'Accept': 'application/pdf'
    });

    const options: any = {
      headers: headers,
      responseType: 'blob',
      observe: 'body'
    };

    return this.http.get(uri, options);
  }

  getGapPremiums(gapPremiumsRequest: GapPremiumsRequest): Observable<GapPremiumsResponse> {
    return this._getGapPremiums(gapPremiumsRequest).pipe(
      map(response => response.body as GapPremiumsResponse)
    );
  }

  private _getGapPremiums(gapPremiumsRequest: GapPremiumsRequest): Observable<StrictHttpResponse<GapPremiumsResponse>> {
    const req = new HttpRequest<GapPremiumsRequest>(
      'POST',
      `${this.rootUrl}/gap/calculatePremiums`,
      gapPremiumsRequest,
      {
        headers: new HttpHeaders(),
        responseType: 'json'
      });

    return this.http.request<GapPremiumsRequest>(req).pipe(
      filter(response => response instanceof HttpResponse),
      map((response) => {
        return response as StrictHttpResponse<GapPremiumsResponse>;
      })
    );
  }

  saveGapQuoteDetails(gapSendQuoteRequest: GapSendQuoteRequest): Observable<StrictHttpResponse<null>> {
    return this._saveGapQuoteDetails(gapSendQuoteRequest).pipe(
      map(response => response.body as null)
    );
  }

  private _saveGapQuoteDetails(gapSendQuoteRequest: GapSendQuoteRequest): Observable<StrictHttpResponse<null>> {
    let _headers: HttpHeaders = new HttpHeaders();
    let _request: HttpRequest<GapSendQuoteRequest> = new HttpRequest<GapSendQuoteRequest>(
      'POST',
      `${this.rootUrl}/gap/sendQuote`,
      gapSendQuoteRequest,
      {
        headers: _headers
      });
    return this.http.request<GapSendQuoteRequest>(_request).pipe(
      filter(response => response instanceof HttpResponse),
      map(response => {
        return response as StrictHttpResponse<null>;
      })
    );
  }

  updateTermsConditions(updateTcsRequest: UpdateTcsRequest): Observable<boolean> {
    return this._updateTermsConditionsPostResponse(updateTcsRequest).pipe(
      map(response => response.body as boolean)
    );
  }

  private _updateTermsConditionsPostResponse(updateTcsRequest: UpdateTcsRequest): Observable<HttpResponse<boolean>> {
    const req = new HttpRequest<UpdateTcsRequest>(
      'POST',
      this.rootUrl + `/gap/updateTermsConditions`,
      updateTcsRequest,
      {
        headers: new HttpHeaders(),
        params: this.newParams(),
        responseType: 'text'
      });

    return this.http.request<any>(req).pipe(
      filter(event => event instanceof HttpResponse),
      map((response) => {
        return (response as HttpResponse<any>).clone({ body: (response as HttpResponse<any>).body === 'true' }) as HttpResponse<boolean>
      })
    );
  }
}
